import clsx from 'clsx';
import { memo } from 'react';

import { getStatusIcon } from '../../../../utils/getStatusIcon';
import { Icon } from '../../../Icon/Icon';
import { Layout } from '../../../Layout/Layout';
import { TextBlock } from '../../../TextBlock/TextBlock';
import './Step.css';

export type StepProps = {
  /** Where this step sits in the sequence. */
  status: 'completed' | 'current' | 'future' | 'critical' | 'loading';
  /** Primary label for the step. */
  text: string;
  /**
   * Orientation of the sequence this step belongs to. Set by `Steps` — a
   * vertical sequence renders each step as an icon-left row instead of the
   * default centered column.
   */
  direction?: 'horizontal' | 'vertical';
  /** Supporting detail below the primary label. */
  secondaryText?: string;
  /**
   * Names the status for assistive tech (e.g. "Completed"). Without it, the
   * completed and future indicators are distinguishable by sight only.
   */
  statusLabel?: string;
  /**
   * Makes the step a real button (never a div with a click handler). Omit
   * for a read-only sequence; the caller owns what a click does (jump to
   * that step, open a popover, etc.).
   */
  onClick?: () => void;
};

function resolveIndicatorIcon(status: StepProps['status']) {
  if (status === 'completed') return 'check';
  if (status === 'critical') return getStatusIcon('critical');
  if (status === 'loading') return 'loading';
  return undefined;
}

export const Step = memo(function Step({
  status,
  text,
  direction = 'horizontal',
  secondaryText,
  statusLabel,
  onClick,
}: StepProps) {
  const isVertical = direction === 'vertical';
  const indicatorIcon = resolveIndicatorIcon(status);

  return (
    <Layout
      align={isVertical ? 'start' : 'stretch'}
      aria-current={onClick && status === 'current' ? 'step' : undefined}
      as={onClick ? 'button' : 'div'}
      className={clsx(
        'eds-step',
        `eds-step--${status}`,
        isVertical && 'eds-step--direction-vertical',
        onClick && 'eds-step--interactive',
      )}
      direction={isVertical ? 'row' : 'column'}
      spacing={isVertical ? 'none' : 's'}
      type={onClick ? 'button' : undefined}
      onClick={onClick}
    >
      <span aria-hidden className="eds-step--indicator">
        {indicatorIcon && (
          <Icon icon={indicatorIcon} size={isVertical ? 'xs' : 's'} />
        )}
      </span>
      {statusLabel && <span className="eds-step--status">{statusLabel}</span>}
      <TextBlock
        secondaryText={secondaryText}
        stroke={status === 'current' ? 'strong' : 'default'}
        text={text}
        truncate={1}
        __private={{ inInteractive: Boolean(onClick) }}
      />
    </Layout>
  );
});
